import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LiveAvatarSession, SessionEvent, AgentEventsEnum } from "@heygen/liveavatar-web-sdk";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, X, Send, Loader2, Volume2, VolumeX, Mic, ArrowRight,
  CalendarClock, FileSearch, BookOpen, Circle, Radio,
} from "lucide-react";

const SUGGESTIONS = [
  { icon: CalendarClock, label: "Meeting → Minutes of Meeting", q: "How do I turn a meeting into a Minutes of Meeting?" },
  { icon: FileSearch, label: "RFQ → Proposal walkthrough", q: "Walk me through creating a proposal from an RFQ" },
  { icon: BookOpen, label: "What is a RAID log?", q: "What is a RAID log?" },
  { icon: Sparkles, label: "Summarize my projects", q: "Summarize my projects" },
];
const CAPABILITIES = [
  { icon: CalendarClock, label: "Meetings & MoM" },
  { icon: FileSearch, label: "RFQ proposals" },
  { icon: BookOpen, label: "Your projects & data" },
];
const MIC_LANGS = [
  { code: "en-US", label: "EN" }, { code: "hi-IN", label: "हि" }, { code: "te-IN", label: "తె" },
  { code: "ta-IN", label: "த" }, { code: "kn-IN", label: "ಕ" }, { code: "ml-IN", label: "മ" },
];
const LOCALE = { en: "en-US", hi: "hi-IN", te: "te-IN", ta: "ta-IN", kn: "kn-IN", ml: "ml-IN" };
// Split spoken text into [firstSentence, rest] so the avatar can start talking after the first part.
function splitForSpeech(text) {
  const t = (text || "").trim();
  if (!t) return [];
  const m = t.match(/^[\s\S]*?[.!?](\s|$)/);
  if (m && m[0].trim().length >= 12 && m[0].trim().length < t.length) {
    return [m[0].trim(), t.slice(m[0].length).trim()].filter(Boolean);
  }
  return [t];
}
const FEMALE_HINTS = ["female", "samantha", "victoria", "karen", "moira", "tessa", "fiona", "zira",
  "aria", "jenny", "neerja", "swara", "google uk english female", "google us english", "serena", "allison"];

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
const fmtTime = (t) => new Date(t || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function MarkdownLite({ text }) {
  return (
    <div className="space-y-1.5">
      {(text || "").split("\n").map((ln, i) => {
        const bullet = /^\s*[-*•]\s+/.test(ln);
        const html = ln.replace(/^\s*[-*•]\s+/, "").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
        if (!ln.trim()) return null;
        return bullet ? (
          <div key={i} className="flex gap-2 text-sm leading-relaxed"><span className="text-[#29abe2] mt-0.5">•</span><span dangerouslySetInnerHTML={{ __html: html }} /></div>
        ) : <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </div>
  );
}
function AriaBadge() {
  return (
    <div className="h-8 w-8 shrink-0 rounded-full brand-gradient grid place-items-center shadow-md ring-2 ring-white/60 dark:ring-white/10">
      <Sparkles size={15} className="text-white" />
    </div>
  );
}

export default function HelpAvatar({ open, onOpenChange }) {
  const [config, setConfig] = useState({ avatar_preview: "", assistant_name: "Aria" });
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [caption, setCaption] = useState("");
  const [muted, setMuted] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("yl_aria_muted") === "1"));
  const [micLang, setMicLang] = useState("en-US");
  const [convoMode, setConvoMode] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("yl_aria_convo") === "1"));
  const [voiceURI, setVoiceURI] = useState(() => (typeof localStorage !== "undefined" ? (localStorage.getItem("yl_aria_voice") || "") : ""));
  const [voiceList, setVoiceList] = useState([]);
  const convId = useRef(`help_${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const greetedRef = useRef(false);
  const capTimer = useRef(null);
  const voicesRef = useRef([]);
  const convoRef = useRef(convoMode);
  const openRef = useRef(open);
  const startListenRef = useRef(null);
  const avatarRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const lastAskRef = useRef({ t: "", at: 0 });
  const speakingRef = useRef(false);
  const pttActiveRef = useRef(false);
  const pttTextRef = useRef("");
  const idleRef = useRef(null);
  const pendingSayRef = useRef("");
  const liveConfiguredRef = useRef(false);
  const speakSeqRef = useRef(0);
  const endResolverRef = useRef(null);
  const chromaRunningRef = useRef(false);
  const startingRef = useRef(false);
  const liveReadyRef = useRef(false);
  const [liveReady, setLiveReady] = useState(false);
  const [videoLive, setVideoLive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [livePaused, setLivePaused] = useState(false);
  useEffect(() => { convoRef.current = convoMode; }, [convoMode]);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { liveReadyRef.current = liveReady; }, [liveReady]);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted, liveReady]);

  useEffect(() => {
    api.get("/help/config").then((r) => { setConfig(r.data); liveConfiguredRef.current = !!r.data.live_avatar; }).catch(() => {});
    const load = () => {
      if (!synth) return;
      const vs = synth.getVoices(); voicesRef.current = vs;
      const allow = ["en", "hi", "te", "ta", "kn", "ml"];
      const filt = vs.filter((v) => allow.includes((v.lang || "").slice(0, 2).toLowerCase()));
      setVoiceList((filt.length ? filt : vs).slice(0, 24));
    };
    load();
    if (synth) synth.onvoiceschanged = load;
    return () => {
      try { synth?.cancel(); } catch { /* noop */ }
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      if (capTimer.current) clearTimeout(capTimer.current);
    };
  }, []);

  const pickVoice = useCallback((lang) => {
    const vs = voicesRef.current.length ? voicesRef.current : (synth ? synth.getVoices() : []);
    if (voiceURI) { const sel = vs.find((v) => v.voiceURI === voiceURI); if (sel) return sel; }
    const two = lang.slice(0, 2).toLowerCase();
    const byLang = vs.filter((v) => (v.lang || "").toLowerCase().startsWith(two));
    const pool = byLang.length ? byLang : vs;
    const indian = pool.find((v) => /india|neerja|en-in|hi-in/i.test((v.lang || "") + (v.name || "")));
    const female = pool.find((v) => FEMALE_HINTS.some((n) => (v.name || "").toLowerCase().includes(n)));
    return indian || female || pool[0] || null;
  }, [voiceURI]);

  const afterSpeak = useCallback(() => {
    if (convoRef.current && openRef.current && startListenRef.current) startListenRef.current();
  }, []);

  const speak = useCallback((text, lang = "en-US") => {
    if (!text) return;
    const clean = text.replace(/\*\*/g, "").replace(/[•*#_>`]/g, "");
    // Live mode: ONLY Anastasia speaks (single female voice). Browser TTS is never used.
    if (liveConfiguredRef.current || config.live_avatar) {
      if (liveReadyRef.current && avatarRef.current) {
        // Faster first words: synthesize sentence-by-sentence (in parallel) and stream each part
        // to Anastasia in order, so she starts talking once the FIRST sentence is ready.
        setSpeaking(true); setCaption(clean);
        speakSeqRef.current += 1;
        const seq = speakSeqRef.current;
        const parts = splitForSpeech(clean);
        const proms = parts.map((p) => api.post("/help/tts", { text: p }).then((r) => r.data?.audio_b64).catch(() => null));
        let i = 0;
        const playNext = async () => {
          if (seq !== speakSeqRef.current) return;
          if (i >= proms.length) { setSpeaking(false); setCaption(""); bumpIdle(); afterSpeak(); return; }
          const b64 = await proms[i++];
          if (seq !== speakSeqRef.current) return;
          if (b64 && avatarRef.current) {
            endResolverRef.current = playNext;
            try { avatarRef.current.repeatAudio(b64); } catch { playNext(); }
          } else { playNext(); }
        };
        playNext();
      } else {
        pendingSayRef.current = clean;   // defer until her stream connects
      }
      return;
    }
    if (!synth) return;
    try { synth.cancel(); } catch { /* noop */ }
    if (muted) return;
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = lang;
    const v = pickVoice(lang);
    if (v) u.voice = v;
    u.rate = 1.03; u.pitch = 1.06;
    u.onend = () => { setSpeaking(false); afterSpeak(); };
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    setCaption(clean);
    if (capTimer.current) clearTimeout(capTimer.current);
    const secs = Math.min(22, Math.max(3, clean.split(/\s+/).length / 2.6));
    capTimer.current = setTimeout(() => { setSpeaking(false); setCaption(""); afterSpeak(); }, secs * 1000);
    try { synth.speak(u); } catch { /* noop */ }
  }, [muted, pickVoice, afterSpeak, config.live_avatar]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopSpeak = useCallback(() => {
    speakSeqRef.current += 1; endResolverRef.current = null;   // abort any in-flight streamed reply
    try { avatarRef.current?.interrupt?.(); } catch { /* noop */ }
    try { synth?.cancel(); } catch { /* noop */ }
    setSpeaking(false); setCaption("");
    if (capTimer.current) clearTimeout(capTimer.current);
  }, []);

  // Idle auto-stop: close the live session after 60s of inactivity to save HeyGen credits.
  const bumpIdle = useCallback(() => {
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(async () => {
      chromaRunningRef.current = false;
      cancelAnimationFrame(rafRef.current);
      const s = avatarRef.current; avatarRef.current = null;
      setLiveReady(false); setVideoLive(false); setListening(false); setLivePaused(true);
      try { await s?.stop?.(); } catch { /* noop */ }
      if (videoRef.current) videoRef.current.srcObject = null;
    }, 60000);
  }, []);

  const startChroma = useCallback(() => {
    const v = videoRef.current, cv = canvasRef.current;
    if (!v || !cv) return;
    const ctx = cv.getContext("2d", { willReadFrequently: true });
    chromaRunningRef.current = true;
    const render = () => {
      if (!chromaRunningRef.current) return;
      if (v.videoWidth) {
        const W = 440, H = Math.max(1, Math.round((W * v.videoHeight) / v.videoWidth));
        if (cv.width !== W) { cv.width = W; cv.height = H; }
        try {
          ctx.drawImage(v, 0, 0, W, H);
          const img = ctx.getImageData(0, 0, W, H), d = img.data;
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i], g = d[i + 1], b = d[i + 2];
            if (g > 78 && g > r * 1.2 && g > b * 1.2) { d[i] = 16; d[i + 1] = 42; d[i + 2] = 92; }   // green screen -> brand blue
            else if (g > r && g > b) d[i + 1] = Math.round((r + b) / 2);                              // suppress green spill
          }
          ctx.putImageData(img, 0, 0);
          setVideoLive(true);
        } catch { /* noop */ }
      }
      rafRef.current = requestAnimationFrame(render);
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(render);
  }, []);

  const startLive = useCallback(async () => {
    if (avatarRef.current || startingRef.current) return;
    startingRef.current = true;
    setConnecting(true);
    setLivePaused(false);
    try {
      const { data } = await api.post("/help/stream-token");
      if (!data?.session_token) throw new Error("no session token");
      const session = new LiveAvatarSession(data.session_token, {
        autoKeepAlive: true,
        voiceChat: { defaultMuted: true },
      });
      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        if (videoRef.current) {
          try { session.attach(videoRef.current); } catch { /* noop */ }
          videoRef.current.muted = muted;
          videoRef.current.play().catch(() => { /* gesture */ });
          startChroma();
        }
        setLiveReady(true); bumpIdle();
        const pend = pendingSayRef.current; pendingSayRef.current = "";
        if (pend) {
          setSpeaking(true); setCaption(pend);
          api.post("/help/tts", { text: pend })
            .then(({ data }) => { if (data?.audio_b64) { try { session.repeatAudio(data.audio_b64); } catch { /* noop */ } } })
            .catch(() => setSpeaking(false));
        }
      });
      session.on(SessionEvent.SESSION_DISCONNECTED, () => { chromaRunningRef.current = false; setLiveReady(false); setVideoLive(false); });
      session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        setSpeaking(true);
        try { recognitionRef.current?.stop(); } catch { /* noop */ }  // echo guard: mute mic while she speaks
        setListening(false);
      });
      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        const r = endResolverRef.current; endResolverRef.current = null;
        if (r) { r(); return; }   // play the next streamed sentence
        setSpeaking(false); setCaption(""); bumpIdle(); afterSpeak();
      });
      session.on(AgentEventsEnum.SESSION_STOPPED, () => { setLiveReady(false); setVideoLive(false); });
      await session.start();
      avatarRef.current = session;
    } catch (e) {
      try { await avatarRef.current?.stop?.(); } catch { /* noop */ }
      avatarRef.current = null; setLiveReady(false);  // fall back to browser TTS
    } finally {
      startingRef.current = false;
      setConnecting(false);
    }
  }, [muted, afterSpeak, startChroma, bumpIdle]);

  const stopLive = useCallback(async () => {
    clearTimeout(idleRef.current);
    chromaRunningRef.current = false;
    cancelAnimationFrame(rafRef.current);
    const s = avatarRef.current;
    avatarRef.current = null; setLiveReady(false); setVideoLive(false); setListening(false);
    try { await s?.stop?.(); } catch { /* noop */ }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => { if (open && config.live_avatar) startLive(); }, [open, config.live_avatar, startLive]);
  useEffect(() => () => { stopLive(); }, [stopLive]);

  // Greeting (text + instant voice) on open
  const fetchGreeting = useCallback(async () => {
    try {
      const { data } = await api.get("/help/greeting");
      if (data.text) {
        setMessages((m) => m.map((x) => (x._greeting ? { ...x, text: data.text } : x)));
        if (!greetedRef.current) { greetedRef.current = true; speak(data.text, "en-US"); }
      }
    } catch { /* keep default greeting */ }
  }, [speak]);

  useEffect(() => {
    if (open) {
      if (messages.length === 0) {
        setMessages([{ role: "assistant", _greeting: true, at: Date.now(), text: `Hi! I'm ${config.assistant_name || "Aria"}, your Business360 guide. Ask me how to use the studio, about your own projects and proposals, or any project-management term.` }]);
      }
      fetchGreeting();
    } else {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setListening(false);
      stopSpeak();
      stopLive();
    }
  }, [open, config.assistant_name]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    const nowMs = Date.now();
    if (question === lastAskRef.current.t && nowMs - lastAskRef.current.at < 3500) return; // dedupe repeat STT
    lastAskRef.current = { t: question, at: nowMs };
    bumpIdle();
    if (livePaused) { setLivePaused(false); startLive(); }
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
    stopSpeak();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question, at: Date.now() }]);
    setBusy(true);
    try {
      const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
      const { data } = await api.post("/help/answer", { conversation_id: convId.current, text: question, history });
      setMessages((m) => [...m, { role: "assistant", text: data.answer, action: data.action, at: Date.now() }]);
      speak(data.speak || data.answer, LOCALE[data.lang] || "en-US");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't answer just now. Please try again.", at: Date.now() }]);
    } finally {
      setBusy(false);
    }
  };

  const startListening = useCallback(() => {
    if (!SR) return;
    stopSpeak();
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    const rec = new SR();
    rec.lang = micLang; rec.interimResults = true; rec.continuous = false;
    let handled = false;
    rec.onresult = (e) => {
      const res = e.results[e.results.length - 1];
      const t = res[0].transcript;
      setInput(t);
      if (res.isFinal && !handled) {
        handled = true;
        setListening(false);
        try { rec.stop(); } catch { /* noop */ }
        if (t.trim()) ask(t.trim());
      }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => {
      setListening(false);
      // Hands-free: keep listening continuously between turns (unless a phrase was captured — Aria's reply restarts it).
      if (convoRef.current && openRef.current && !handled && !speakingRef.current) {
        setTimeout(() => { if (convoRef.current && openRef.current) startListening(); }, 250);
      }
    };
    recognitionRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  }, [micLang, stopSpeak]); // eslint-disable-line
  const pttStart = useCallback(() => {
    if (!SR || pttActiveRef.current) return;
    pttActiveRef.current = true;
    stopSpeak();
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    pttTextRef.current = "";
    const rec = new SR();
    rec.lang = micLang; rec.interimResults = true; rec.continuous = true;
    rec.onresult = (e) => { const t = Array.from(e.results).map((r) => r[0].transcript).join(" "); pttTextRef.current = t; setInput(t); };
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    try { rec.start(); setListening(true); } catch { pttActiveRef.current = false; setListening(false); }
  }, [micLang, stopSpeak]);

  const pttEnd = useCallback(() => {
    if (!pttActiveRef.current) return;
    pttActiveRef.current = false;
    setListening(false);
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    const t = (pttTextRef.current || "").trim();
    if (t) ask(t);
  }, []); // eslint-disable-line

  useEffect(() => { startListenRef.current = startListening; }, [startListening]);

  const toggleMic = () => {
    if (!SR) return;
    if (listening) { try { recognitionRef.current?.stop(); } catch { /* noop */ } setListening(false); return; }
    startListening();
  };

  const toggleMute = () => {
    setMuted((m) => {
      const nv = !m;
      try { localStorage.setItem("yl_aria_muted", nv ? "1" : "0"); } catch { /* noop */ }
      if (nv) stopSpeak();
      return nv;
    });
  };

  const goAction = (a) => { onOpenChange(false); navigate(a.route); };
  if (!open) return null;

  return (
    <div className="fixed bottom-24 right-4 lg:right-6 z-50 w-[calc(100vw-2rem)] max-w-[420px] animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="help-avatar-panel">
      <style>{`
        @keyframes ariaWave{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}
        .aria-wbar{width:3px;border-radius:3px;background:#fff;transform-origin:bottom;animation:ariaWave .85s ease-in-out infinite}
        @keyframes ariaPulseRing{0%{box-shadow:0 0 0 0 rgba(41,171,226,.5)}70%{box-shadow:0 0 0 16px rgba(41,171,226,0)}100%{box-shadow:0 0 0 0 rgba(41,171,226,0)}}
        .aria-ring{animation:ariaPulseRing 1.6s infinite}
      `}</style>
      <div className="rounded-[24px] overflow-hidden shadow-[0_24px_60px_-12px_rgba(15,37,87,0.45)] dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] ring-1 ring-black/10 dark:ring-white/10 bg-background flex flex-col max-h-[82vh]">
        {/* Avatar stage */}
        <div className={`relative ${speaking ? "aria-ring" : ""}`}>
          <div className="aspect-video bg-gradient-to-br from-[#0f2557] via-[#1a3880] to-[#29abe2] relative overflow-hidden cursor-pointer" onClick={() => { if (speaking) { stopSpeak(); if (convoMode) startListening(); } }} title="Tap to interrupt Aria">
            {config.live_avatar && (
              <video ref={videoRef} autoPlay playsInline data-testid="help-avatar-video"
                onEmptied={() => setVideoLive(false)}
                className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none" />
            )}
            {config.live_avatar && (
              <canvas ref={canvasRef} data-testid="help-avatar-canvas"
                className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 ${videoLive ? "opacity-100" : "opacity-0"}`} />
            )}
            <img src={config.avatar_preview} alt="Aria" data-testid="help-avatar-portrait"
              className={`w-full h-full object-cover transition-all duration-500 ${config.live_avatar && videoLive ? "opacity-0" : "opacity-100"} ${speaking ? "scale-[1.03]" : "scale-100"}`}
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
            {config.live_avatar && connecting && !liveReady && (
              <div className="absolute inset-0 grid place-items-center" data-testid="help-avatar-connecting">
                <div className="flex items-center gap-2 text-white/90 text-xs bg-black/40 rounded-full px-3 py-1.5"><Loader2 size={13} className="animate-spin" /> Connecting Anastasia…</div>
              </div>
            )}
            {config.live_avatar && livePaused && (
              <div className="absolute inset-0 grid place-items-center bg-black/45 backdrop-blur-sm" data-testid="help-avatar-paused">
                <button onClick={() => { setLivePaused(false); startLive(); }} data-testid="help-avatar-resume"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#29abe2] hover:bg-[#1a7bb0] rounded-full px-4 py-2 shadow-lg transition-colors">
                  <Radio size={15} /> Resume Anastasia
                </button>
              </div>
            )}
            <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-black/55 via-black/20 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
            {speaking && !(config.live_avatar && liveReady) && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-end gap-1 h-6" aria-hidden>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <span key={i} className="aria-wbar" style={{ animationDelay: `${i * 0.09}s`, height: `${10 + (i % 4) * 6}px` }} />
                ))}
              </div>
            )}
          </div>
          {/* caption */}
          {speaking && caption && (
            <div className="absolute bottom-10 inset-x-3" data-testid="help-avatar-caption">
              <div className="mx-auto max-w-[94%] text-center text-white text-[11px] leading-snug bg-black/45 backdrop-blur-sm rounded-lg px-3 py-1.5 line-clamp-2">{caption}</div>
            </div>
          )}
          {/* header */}
          <div className="absolute top-0 inset-x-0 flex items-center justify-between p-3.5">
            <div className="flex items-center gap-2.5 text-white">
              <div className="relative"><AriaBadge /><span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0a1a3a]" /></div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">{config.assistant_name || "Aria"}</div>
                <div className="text-[10px] text-white/80 flex items-center gap-1"><Circle size={6} className="fill-emerald-400 text-emerald-400" />Live · {speaking ? "speaking" : "ready"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={toggleMute} data-testid="help-avatar-mute" aria-label={muted ? "Unmute Aria" : "Mute Aria"}
                className="h-8 w-8 grid place-items-center rounded-xl bg-black/30 hover:bg-black/50 backdrop-blur-md border border-white/15 text-white shadow-sm transition-all duration-200">
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
              <button onClick={() => onOpenChange(false)} data-testid="help-avatar-close"
                className="h-8 w-8 grid place-items-center rounded-xl bg-black/30 hover:bg-black/50 backdrop-blur-md border border-white/15 text-white shadow-sm transition-all duration-200"><X size={15} /></button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3.5 space-y-3.5 min-h-[170px] bg-gradient-to-b from-secondary/30 via-background/80 to-background" data-testid="help-avatar-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <AriaBadge />}
              <div className="max-w-[82%]">
                <div className={`rounded-2xl px-3.5 py-2.5 shadow-sm ${m.role === "user" ? "bg-[#0f2557] dark:bg-[#122c66] text-white rounded-br-sm" : "bg-background border border-black/5 dark:border-white/10 text-foreground rounded-bl-sm"}`}>
                  {m.role === "assistant" ? (
                    <>
                      <MarkdownLite text={m.text} />
                      {m.action?.route && (
                        <button onClick={() => goAction(m.action)} data-testid="help-avatar-action"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#29abe2] hover:bg-[#1a7bb0] rounded-full px-3 py-1.5 transition-colors shadow-sm">
                          {m.action.label} <ArrowRight size={13} />
                        </button>
                      )}
                    </>
                  ) : <p className="text-sm">{m.text}</p>}
                </div>
                <div className={`text-[10px] text-muted-foreground mt-1 ${m.role === "user" ? "text-right" : "text-left"}`}>{m.role === "assistant" ? "Aria · " : ""}{fmtTime(m.at)}</div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex items-end gap-2 justify-start">
              <AriaBadge />
              <div className="bg-background border border-black/5 dark:border-white/10 rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-1 shadow-sm">
                {[0, 1, 2].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-[#29abe2] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
              </div>
            </div>
          )}
          {messages.length <= 1 && !busy && (
            <div className="pt-1 space-y-2.5">
              <div className="flex flex-wrap gap-1.5">
                {CAPABILITIES.map((c) => (
                  <span key={c.label} className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-secondary text-foreground/80 rounded-full px-2.5 py-1"><c.icon size={12} className="text-[#29abe2]" /> {c.label}</span>
                ))}
              </div>
              <div className="text-[11px] font-semibold text-muted-foreground pt-1">Try asking</div>
              <div className="grid gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s.label} onClick={() => ask(s.q)} data-testid="help-suggestion"
                    className="group flex items-center gap-2.5 text-left text-xs rounded-xl border border-[#29abe2]/25 bg-background hover:bg-[#29abe2]/10 hover:border-[#29abe2]/60 px-3 py-2.5 shadow-sm transition-all duration-200">
                    <span className="h-6 w-6 shrink-0 rounded-lg bg-[#29abe2]/10 grid place-items-center group-hover:bg-[#29abe2]/20"><s.icon size={13} className="text-[#1a7bb0]" /></span>
                    <span className="text-foreground/90 font-medium">{s.label}</span>
                    <ArrowRight size={13} className="ml-auto text-muted-foreground group-hover:text-[#29abe2] group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3.5 border-t bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            {SR && (
              <div className="flex items-center shrink-0 rounded-full border overflow-hidden h-10">
                <select value={micLang} onChange={(e) => setMicLang(e.target.value)} data-testid="help-avatar-mic-lang" aria-label="Voice input language"
                  className="h-full bg-transparent text-xs pl-2.5 pr-1 outline-none cursor-pointer appearance-none text-foreground">
                  {MIC_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <button onClick={toggleMic} data-testid="help-avatar-mic" aria-label="Speak to Aria"
                  className={`h-8 w-8 mr-1 rounded-full grid place-items-center transition-colors ${listening ? "bg-red-500 text-white" : "hover:bg-secondary text-foreground"}`}>
                  <Mic size={15} className={listening ? "animate-pulse" : ""} />
                </button>
              </div>
            )}
            <Input value={input} onChange={(e) => { setInput(e.target.value); bumpIdle(); }} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={listening ? "Listening…" : "Ask Aria anything…"} data-testid="help-avatar-input" className="rounded-full h-10" />
            <Button size="icon" className="rounded-full shrink-0 h-10 w-10 bg-gradient-to-br from-[#29abe2] to-[#1a7bb0] hover:opacity-90 shadow-md shadow-cyan-500/30 transition-all" onClick={() => ask()} disabled={busy || !input.trim()} data-testid="help-avatar-send">
              <Send size={15} />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            {config.live_avatar ? (
              <span data-testid="help-avatar-voice-label"
                className="flex-1 min-w-0 h-8 inline-flex items-center gap-1.5 text-[11px] font-medium rounded-lg border border-[#29abe2]/30 bg-[#29abe2]/10 px-2.5 text-foreground/80 truncate">
                <Volume2 size={12} className="text-[#1a7bb0] shrink-0" /> Anastasia · warm American voice
              </span>
            ) : (
              <select value={voiceURI} onChange={(e) => { setVoiceURI(e.target.value); try { localStorage.setItem("yl_aria_voice", e.target.value); } catch { /* noop */ } }} data-testid="help-avatar-voice" aria-label="Aria voice"
                className="flex-1 min-w-0 h-8 text-[11px] rounded-lg border bg-background px-2 text-foreground outline-none">
                <option value="">Auto voice (recommended)</option>
                {voiceList.map((v) => <option key={v.voiceURI} value={v.voiceURI}>{v.name} · {v.lang}</option>)}
              </select>
            )}
            {SR && (
              <button onMouseDown={pttStart} onMouseUp={pttEnd} onMouseLeave={pttEnd}
                onTouchStart={(e) => { e.preventDefault(); pttStart(); }} onTouchEnd={(e) => { e.preventDefault(); pttEnd(); }}
                data-testid="help-avatar-ptt" aria-label="Hold to talk"
                className={`shrink-0 h-8 px-2.5 rounded-lg text-[11px] font-medium inline-flex items-center gap-1.5 border transition-all duration-200 select-none ${listening && pttActiveRef.current ? "bg-red-500 text-white border-red-500" : "text-foreground hover:bg-secondary"}`}>
                <Mic size={12} /> Hold
              </button>
            )}
            <button onClick={() => { const nv = !convoMode; setConvoMode(nv); try { localStorage.setItem("yl_aria_convo", nv ? "1" : "0"); } catch { /* noop */ } if (nv) startListening(); else { try { recognitionRef.current?.stop(); } catch { /* noop */ } } }}
              data-testid="help-avatar-handsfree" aria-pressed={convoMode}
              className={`shrink-0 h-8 px-2.5 rounded-lg text-[11px] font-medium inline-flex items-center gap-1.5 border transition-colors ${convoMode ? "bg-[#29abe2] text-white border-[#29abe2]" : "text-foreground hover:bg-secondary"}`}>
              <Radio size={12} /> Hands-free
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">{convoMode ? "Hands-free on · speak naturally; tap the avatar to interrupt" : "Aria answers & speaks instantly · tap the avatar to interrupt"}</p>
        </div>
      </div>
    </div>
  );
}
